import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Zap, X, Check } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { cn } from '@/core/lib/utils';
import { useSavedFilterSets } from '../../hooks/useTaskViewStore';
import { ConditionRow } from './ConditionRow';
import { FieldPickerMenu } from './FieldPickerMenu';
import type {
  FilterCondition,
  FilterFieldType,
  FilterOperator,
  SavedFilterSet,
  TaskFilters,
  TaskPriority,
} from '../../types';

// ─── Converters ───────────────────────────────────────────────────────────────

function filtersToConditions(filters: TaskFilters): FilterCondition[] {
  const ops = filters.operatorsByField ?? {};
  const conditions: FilterCondition[] = [];
  if (filters.statuses.length > 0)
    conditions.push({
      id: crypto.randomUUID(),
      field: 'status',
      operator: ops.status ?? 'is',
      values: filters.statuses,
    });
  if ((filters.priorities?.length ?? 0) > 0)
    conditions.push({
      id: crypto.randomUUID(),
      field: 'priority',
      operator: ops.priority ?? 'is',
      values: filters.priorities as string[],
    });
  if (filters.assigneeIds.length > 0)
    conditions.push({
      id: crypto.randomUUID(),
      field: 'assignee',
      operator: ops.assignee ?? 'is',
      values: filters.assigneeIds,
    });
  if ((filters.tagIds?.length ?? 0) > 0)
    conditions.push({
      id: crypto.randomUUID(),
      field: 'tag',
      operator: ops.tag ?? 'is',
      values: filters.tagIds!,
    });
  if (filters.dueDateFrom || filters.dueDateTo)
    conditions.push({
      id: crypto.randomUUID(),
      field: 'dueDate',
      operator: ops.dueDate ?? 'is',
      values: [filters.dueDateFrom ?? '', filters.dueDateTo ?? ''],
    });
  if (filters.startDateFrom || filters.startDateTo)
    conditions.push({
      id: crypto.randomUUID(),
      field: 'startDate',
      operator: ops.startDate ?? 'is',
      values: [filters.startDateFrom ?? '', filters.startDateTo ?? ''],
    });
  if (filters.titleValue)
    conditions.push({
      id: crypto.randomUUID(),
      field: 'title',
      operator: ops.title ?? 'is',
      values: [filters.titleValue],
    });
  if ((filters.groupIds?.length ?? 0) > 0)
    conditions.push({
      id: crypto.randomUUID(),
      field: 'group',
      operator: ops.group ?? 'is',
      values: filters.groupIds!,
    });
  if ((filters.boardIds?.length ?? 0) > 0)
    conditions.push({
      id: crypto.randomUUID(),
      field: 'board',
      operator: ops.board ?? 'is',
      values: filters.boardIds!,
    });
  return conditions;
}

function conditionsToFilters(
  conditions: FilterCondition[],
  logic: 'and' | 'or',
  search: string,
): TaskFilters {
  const result: TaskFilters = { assigneeIds: [], statuses: [], search, filterLogic: logic };
  const operatorsByField: Partial<Record<FilterFieldType, FilterOperator>> = {};
  for (const cond of conditions) {
    const op = cond.operator ?? 'is';
    if (cond.field === 'status' && cond.values.length > 0) {
      result.statuses = cond.values;
      operatorsByField.status = op;
    } else if (cond.field === 'priority' && cond.values.length > 0) {
      result.priorities = cond.values as TaskPriority[];
      operatorsByField.priority = op;
    } else if (cond.field === 'assignee' && cond.values.length > 0) {
      result.assigneeIds = cond.values;
      operatorsByField.assignee = op;
    } else if (cond.field === 'tag' && cond.values.length > 0) {
      result.tagIds = cond.values;
      operatorsByField.tag = op;
    } else if (cond.field === 'dueDate') {
      result.dueDateFrom = cond.values[0] || undefined;
      result.dueDateTo = cond.values[1] || undefined;
      operatorsByField.dueDate = op;
    } else if (cond.field === 'startDate') {
      result.startDateFrom = cond.values[0] || undefined;
      result.startDateTo = cond.values[1] || undefined;
      operatorsByField.startDate = op;
    } else if (cond.field === 'title' && cond.values[0]) {
      result.titleValue = cond.values[0];
      operatorsByField.title = op;
    } else if (cond.field === 'group' && cond.values.length > 0) {
      result.groupIds = cond.values;
      operatorsByField.group = op;
    } else if (cond.field === 'board' && cond.values.length > 0) {
      result.boardIds = cond.values;
      operatorsByField.board = op;
    }
  }
  result.operatorsByField = operatorsByField;
  return result;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface PanelHeaderProps {
  activeCount: number;
  hasConditions: boolean;
  saveName: string | null;
  onSaveClick: () => void;
  onClose: () => void;
}

function PanelHeader({
  activeCount,
  hasConditions,
  saveName,
  onSaveClick,
  onClose,
}: PanelHeaderProps) {
  const { t } = useTranslation('tasks');
  return (
    <div className="flex items-center gap-2 border-b px-4 py-3">
      <span className="text-sm font-medium">{t('filterPanel.advancedTitle')}</span>
      {activeCount > 0 && (
        <span className="flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-semibold text-primary-foreground">
          {activeCount}
        </span>
      )}
      <div className="ml-auto flex items-center gap-1">
        {hasConditions && saveName === null && (
          <Button
            size="sm"
            variant="ghost"
            className="h-6 gap-1 px-2 text-xs"
            onClick={onSaveClick}
            data-testid="tasks-filter-panel-save"
          >
            {t('filterPanel.save')}
          </Button>
        )}
        <Button
          size="icon"
          variant="ghost"
          className="h-6 w-6"
          onClick={onClose}
          aria-label={t('dialog.closeAria')}
          data-testid="tasks-filter-panel-close"
        >
          <X className="size-3.5" />
        </Button>
      </div>
    </div>
  );
}

interface LogicToggleProps {
  logic: 'and' | 'or';
  onChange: (next: 'and' | 'or') => void;
}

function LogicToggle({ logic, onChange }: LogicToggleProps) {
  const { t } = useTranslation('tasks');
  return (
    <div className="flex items-center gap-2 border-b px-4 py-2">
      <span className="text-xs text-muted-foreground">{t('filterPanel.logic')}</span>
      <div className="flex overflow-hidden rounded-md border text-xs">
        <button
          type="button"
          data-testid="tasks-filter-panel-logic-and"
          className={cn(
            'px-2.5 py-0.5 transition-colors',
            logic === 'and' ? 'bg-primary text-primary-foreground' : 'hover:bg-accent',
          )}
          onClick={() => onChange('and')}
        >
          {t('filterPanel.logicAnd')}
        </button>
        <button
          type="button"
          data-testid="tasks-filter-panel-logic-or"
          className={cn(
            'px-2.5 py-0.5 transition-colors',
            logic === 'or' ? 'bg-primary text-primary-foreground' : 'hover:bg-accent',
          )}
          onClick={() => onChange('or')}
        >
          {t('filterPanel.logicOr')}
        </button>
      </div>
    </div>
  );
}

interface SavedFilterListProps {
  savedFilterSets: SavedFilterSet[];
  onApply: (set: SavedFilterSet) => void;
  onRemove: (id: string) => void;
}

function SavedFilterList({ savedFilterSets, onApply, onRemove }: SavedFilterListProps) {
  const { t } = useTranslation('tasks');
  return (
    <div className="flex flex-col px-3 py-2">
      <p className="mb-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
        {t('filterPanel.savedFilters')}
      </p>
      {savedFilterSets.length === 0 ? (
        <p className="py-1 text-xs text-muted-foreground">{t('filterPanel.noSavedFilters')}</p>
      ) : (
        <div className="flex flex-col gap-0.5">
          {savedFilterSets.map((set) => (
            <div
              key={set.id}
              className="group/saved flex items-center gap-2 rounded px-2 py-1.5 hover:bg-accent"
            >
              <button
                type="button"
                data-testid={`tasks-filter-saved-${set.id}`}
                className="flex flex-1 items-center gap-2 text-left"
                onClick={() => onApply(set)}
              >
                <Zap className="size-3 shrink-0 text-primary" />
                <span className="flex-1 truncate text-xs">{set.name}</span>
                <span className="shrink-0 text-[10px] text-muted-foreground">
                  {t('filterPanel.savedCount', { count: set.conditions.length })}
                </span>
              </button>
              <button
                type="button"
                data-testid={`tasks-filter-saved-${set.id}-remove`}
                className="hidden shrink-0 group-hover/saved:block"
                onClick={() => onRemove(set.id)}
                aria-label={t('filterPanel.removeSavedFilter')}
              >
                <X className="size-3 text-muted-foreground hover:text-destructive" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

interface TaskFilterPanelProps {
  projectId: string;
  boardId: string;
  filters: TaskFilters;
  onChange: (filters: TaskFilters) => void;
  onClose: () => void;
}

export function TaskFilterPanel({
  projectId,
  boardId,
  filters,
  onChange,
  onClose,
}: TaskFilterPanelProps) {
  const { t } = useTranslation('tasks');
  const { savedFilterSets, setSavedFilterSets } = useSavedFilterSets(boardId);

  const [conditions, setConditions] = useState<FilterCondition[]>(() =>
    filtersToConditions(filters),
  );
  const [logic, setLogic] = useState<'and' | 'or'>(filters.filterLogic ?? 'and');
  const [view, setView] = useState<'conditions' | 'fieldPicker'>('conditions');
  const [saveName, setSaveName] = useState<string | null>(null);

  const hasConditions = conditions.length > 0;
  const activeCount = conditions.filter((c) => c.values.length > 0).length;
  const usedFields = conditions.map((c) => c.field);

  function applyConditions(next: FilterCondition[], nextLogic: 'and' | 'or') {
    onChange(conditionsToFilters(next, nextLogic, filters.search));
  }

  function handleAddField(field: FilterFieldType) {
    const next = [
      ...conditions,
      { id: crypto.randomUUID(), field, operator: 'is' as FilterOperator, values: [] },
    ];
    setConditions(next);
    applyConditions(next, logic);
    setView('conditions');
  }

  function handleConditionChange(id: string, values: string[]) {
    const next = conditions.map((c) => (c.id === id ? { ...c, values } : c));
    setConditions(next);
    applyConditions(next, logic);
  }

  function handleOperatorChange(id: string, operator: FilterOperator) {
    const next = conditions.map((c) => (c.id === id ? { ...c, operator } : c));
    setConditions(next);
    applyConditions(next, logic);
  }

  function handleConditionRemove(id: string) {
    const next = conditions.filter((c) => c.id !== id);
    setConditions(next);
    applyConditions(next, logic);
  }

  function handleLogicChange(next: 'and' | 'or') {
    setLogic(next);
    applyConditions(conditions, next);
  }

  function handleClearAll() {
    setConditions([]);
    setLogic('and');
    onChange({ assigneeIds: [], statuses: [], search: filters.search });
  }

  function handleSaveFilter() {
    if (!saveName?.trim()) return;
    setSavedFilterSets([
      ...savedFilterSets,
      { id: crypto.randomUUID(), name: saveName.trim(), conditions, logic },
    ]);
    setSaveName(null);
  }

  if (view === 'fieldPicker') {
    return (
      <div className="w-80" data-testid="tasks-filter-panel">
        <FieldPickerMenu
          usedFields={usedFields}
          onSelect={handleAddField}
          onClose={() => setView('conditions')}
        />
      </div>
    );
  }

  return (
    <div className="flex w-80 flex-col" data-testid="tasks-filter-panel">
      <PanelHeader
        activeCount={activeCount}
        hasConditions={hasConditions}
        saveName={saveName}
        onSaveClick={() => setSaveName('')}
        onClose={onClose}
      />

      {saveName !== null && (
        <div className="flex items-center gap-1.5 border-b px-3 py-2">
          <Input
            autoFocus
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSaveFilter();
              if (e.key === 'Escape') setSaveName(null);
            }}
            placeholder={t('filterPanel.saveName')}
            className="h-6 flex-1 text-xs"
            data-testid="tasks-filter-panel-save-input"
          />
          <Button
            size="icon"
            variant="ghost"
            className="h-6 w-6 shrink-0"
            onClick={handleSaveFilter}
            disabled={!saveName.trim()}
          >
            <Check className="size-3.5 text-primary" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-6 w-6 shrink-0"
            onClick={() => setSaveName(null)}
          >
            <X className="size-3.5" />
          </Button>
        </div>
      )}

      {hasConditions && <LogicToggle logic={logic} onChange={handleLogicChange} />}

      {hasConditions && (
        <div className="flex flex-col gap-0.5 px-3 pt-2">
          <p className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">
            {t('filterPanel.conditions')}
          </p>
          {conditions.map((cond, i) => (
            <ConditionRow
              key={cond.id}
              condition={cond}
              index={i}
              logic={logic}
              projectId={projectId}
              boardId={boardId}
              onChange={(values) => handleConditionChange(cond.id, values)}
              onOperatorChange={(op) => handleOperatorChange(cond.id, op)}
              onRemove={() => handleConditionRemove(cond.id)}
            />
          ))}
        </div>
      )}

      <div className="border-b px-3 py-2">
        <button
          type="button"
          data-testid="tasks-filter-add-condition"
          onClick={() => setView('fieldPicker')}
          disabled={usedFields.length === 9}
          className="flex items-center gap-1.5 py-1 text-xs text-primary hover:text-primary/80 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Plus className="size-3.5" />
          {t('filterPanel.addCondition')}
        </button>
      </div>

      <SavedFilterList
        savedFilterSets={savedFilterSets}
        onApply={(set) => {
          setConditions(set.conditions);
          setLogic(set.logic);
          applyConditions(set.conditions, set.logic);
        }}
        onRemove={(id) => setSavedFilterSets(savedFilterSets.filter((s) => s.id !== id))}
      />

      {hasConditions && (
        <div className="flex justify-end border-t px-3 py-2">
          <button
            type="button"
            data-testid="tasks-filter-clear-all"
            onClick={handleClearAll}
            className="rounded bg-destructive/10 px-3 py-1.5 text-xs font-medium text-destructive transition-colors hover:bg-destructive/20"
          >
            {t('filterPanel.clearAll')}
          </button>
        </div>
      )}
    </div>
  );
}
